import React, { useState } from 'react';

import { Modal, ModalActions } from '../../ui/Modal/Modal';
import type { AccessOrganization } from '../../../types/access';
import styles from '../UserFormModal/UserFormModal.module.scss';
import ownStyles from './OrganizationFormModal.module.scss';

export interface OrganizationFormValues {
    name: string;
    inn: string;
    requestsLimit: number;
}

interface OrganizationFormModalProps {
    organization: AccessOrganization | null;
    isSaving: boolean;
    onSubmit: (values: OrganizationFormValues) => void;
    onClose: () => void;
}

const INN_PATTERN = /^(\d{10}|\d{12})$/;

export function OrganizationFormModal({
    organization,
    isSaving,
    onSubmit,
    onClose,
}: OrganizationFormModalProps) {
    const [values, setValues] = useState<OrganizationFormValues>({
        name: organization?.name ?? '',
        inn: organization?.inn ?? '',
        requestsLimit: organization?.requestsLimit ?? 0,
    });

    const [touched, setTouched] = useState(false);

    const errors = {
        name: values.name.trim() === '' ? 'Укажите наименование' : null,
        inn: values.inn === '' || INN_PATTERN.test(values.inn) ? null : 'ИНН — 10 или 12 цифр',
    };

    const isValid = Object.values(errors).every((error) => error === null);

    const users = organization?.users ?? [];

    function submit(): void {
        setTouched(true);

        if (isValid && !isSaving) {
            onSubmit({ ...values, name: values.name.trim() });
        }
    }

    return (
        <Modal
            open
            title={organization ? 'Редактировать организацию' : 'Новая организация'}
            onClose={onClose}
            footer={
                <ModalActions>
                    <button type="button" className={styles.secondary} onClick={onClose}>
                        Отмена
                    </button>
                    <button type="button" className={styles.primary} disabled={isSaving} onClick={submit}>
                        {isSaving ? 'Сохраняем…' : 'Сохранить'}
                    </button>
                </ModalActions>
            }
        >
            <div className={styles.form}>
                <label className={styles.field}>
                    <span className={styles.label}>Наименование</span>
                    <input
                        className={styles.input}
                        value={values.name}
                        onChange={(event) => setValues((v) => ({ ...v, name: event.target.value }))}
                    />
                    {touched && errors.name && <span className={styles.error}>{errors.name}</span>}
                </label>

                <label className={styles.field}>
                    <span className={styles.label}>ИНН</span>
                    <input
                        className={styles.input}
                        inputMode="numeric"
                        value={values.inn}
                        onChange={(event) =>
                            setValues((v) => ({ ...v, inn: event.target.value.replace(/\D/g, '') }))
                        }
                    />
                    {touched && errors.inn && <span className={styles.error}>{errors.inn}</span>}
                </label>

                <label className={styles.field}>
                    <span className={styles.label}>Лимит запросов</span>
                    <input
                        className={styles.input}
                        type="number"
                        min={0}
                        value={values.requestsLimit}
                        onChange={(event) =>
                            setValues((v) => ({ ...v, requestsLimit: Number(event.target.value) }))
                        }
                    />
                </label>

                {organization && (
                    <div className={styles.field}>
                        <span className={styles.label}>Пользователи ({users.length})</span>

                        {users.length === 0 ? (
                            <span className={ownStyles.empty}>Пользователи не добавлены</span>
                        ) : (
                            <ul className={ownStyles.userList}>
                                {users.map((user) => (
                                    <li key={user.id} className={ownStyles.userItem}>
                                        <span className={ownStyles.userName}>{user.fullName}</span>
                                        <a className={ownStyles.userEmail} href={`mailto:${user.email}`}>
                                            {user.email}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}